import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { getProjectActionCenter, getProjectActionPath } from '../utils/projectActionCenter';
import { PROJECT_STATUSES } from '../constants/projectWorkflow';
import WorkflowGatePanel from './WorkflowGatePanel';
import OperationalStatusPanel from './OperationalStatusPanel';
import { ProjectWorkspaceProvider } from './ProjectWorkspaceContext';
import './ProjectWorkspaceShell.css';

const NAV_GROUPS = [
    {
        step: '1',
        label: 'Prüfen',
        items: [
            { id: 'initial-consultation', label: 'Erstgespräch', path: 'initial-consultation' },
            { id: 'funding-potential', label: 'Förderpotenzial', path: 'funding-potential' },
            { id: 'audit', label: 'Audit', path: 'audit' }
        ]
    },
    {
        step: '2',
        label: 'Input sichern',
        items: [
            { id: 'customer-portal', label: 'Kundenportal', path: 'customer-portal' },
            { id: 'case-file', label: 'Projektakte', path: 'case-file' },
            { id: 'evidence', label: 'Nachweise', path: 'evidence' },
            { id: 'reviewer-questions', label: 'Gutachter-Rückfragen', path: 'reviewer-questions' }
        ]
    },
    {
        step: '3',
        label: 'Antrag bauen',
        items: [
            { id: 'edit', label: 'Planung', path: 'edit' },
            { id: 'description', label: 'Beschreibung', path: 'description' }
        ]
    },
    {
        step: '4',
        label: 'Einreichen & betreiben',
        items: [
            { id: 'overview', label: 'Übersicht', path: 'overview' },
            { id: 'abrechnungen', label: 'Abrechnung', path: 'abrechnungen' },
        ]
    }
];

const getDecisionTone = (decision = '') => {
    const normalized = String(decision).toLowerCase();
    if (normalized.includes('go') || normalized.includes('freigabe')) return 'success';
    if (normalized.includes('stop')) return 'danger';
    if (normalized.includes('review') || normalized.includes('senior')) return 'warning';
    return 'neutral';
};

const COMPANY_NAME_FIELDS = ['Firmennamen', 'Unternehmensnamen', 'Unternehmensname', 'Unternehmen Name', 'Unternehmen', 'Firmenname', 'Name des Unternehmens', 'Name der Firma', 'Name', 'Firma', 'Kunde', 'Kundenname', 'Antragsteller'];
const EMPTY_PROJECT_METADATA = { name: '', companyId: '', fundingProgramId: '' };

const getDisplayValue = (value) => {
    if (Array.isArray(value)) return value.map(getDisplayValue).filter(Boolean).join(', ');
    if (value && typeof value === 'object') return value.name || value.label || value.id || '';
    return value === undefined || value === null ? '' : String(value);
};

const getCompanyOptionName = (record = {}) => {
    const fields = record.fields || {};
    for (const fieldName of COMPANY_NAME_FIELDS) {
        const value = getDisplayValue(fields[fieldName]).trim();
        if (value && !/^rec[A-Za-z0-9]{8,}$/.test(value)) return value;
    }
    return 'Unternehmen ohne Name';
};

const projectMetadataEquals = (left = EMPTY_PROJECT_METADATA, right = EMPTY_PROJECT_METADATA) => (
    left.name === right.name &&
    left.companyId === right.companyId &&
    left.fundingProgramId === right.fundingProgramId
);

const ProjectWorkspaceShell = ({ activeSection = 'dashboard', children, onProjectUpdated }) => {
    const { projectId } = useParams();
    const [searchParams, setSearchParams] = useSearchParams();
    const [workspaceData, setWorkspaceData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [statusSaving, setStatusSaving] = useState(false);
    const [statusMessage, setStatusMessage] = useState('');
    const [workspaceError, setWorkspaceError] = useState('');
    const [metadataEditing, setMetadataEditing] = useState(false);
    const [metadataDraft, setMetadataDraft] = useState(EMPTY_PROJECT_METADATA);
    const [metadataOptions, setMetadataOptions] = useState({ companies: [], programs: [], loading: false, loaded: false, error: '' });
    const [metadataSaveState, setMetadataSaveState] = useState({ status: 'idle', message: '' });
    const [operationalDialogOpen, setOperationalDialogOpen] = useState(false);
    const [operationalSaving, setOperationalSaving] = useState(false);
    const [operationalForm, setOperationalForm] = useState({
        eventKey: '',
        waitingOnLabel: '',
        expectedAt: '',
        followUpAt: '',
        responsible: '',
        note: ''
    });
    const workspaceRequestRef = useRef(null);
    const metadataDraftRef = useRef(EMPTY_PROJECT_METADATA);
    const metadataSavedRef = useRef(EMPTY_PROJECT_METADATA);
    const metadataSavingRef = useRef(false);
    const metadataSaveTimerRef = useRef(null);
    const metadataProjectRef = useRef('');
    const componentMountedRef = useRef(true);

    const loadWorkspaceData = useCallback(async ({ silent = false } = {}) => {
        if (!projectId) {
            setLoading(false);
            return null;
        }

        workspaceRequestRef.current?.abort();
        const requestController = new AbortController();
        workspaceRequestRef.current = requestController;
        if (!silent) {
            setLoading(true);
        }
        setWorkspaceError('');
        try {
            const response = await axios.get(`/api/airtable/customer-portal/${projectId}`, {
                signal: requestController.signal
            });
            setWorkspaceData(response.data);
            return response.data;
        } catch (error) {
            if (error?.code === 'ERR_CANCELED') return null;
            console.error('Fehler beim Laden des Projekt-Workspace:', error.response ? error.response.data : error.message);
            setWorkspaceData(null);
            setWorkspaceError(error.response?.data?.error || 'Projekt-Workspace konnte nicht geladen werden.');
            return null;
        } finally {
            if (workspaceRequestRef.current === requestController) {
                workspaceRequestRef.current = null;
                setLoading(false);
            }
        }
    }, [projectId]);

    useEffect(() => {
        loadWorkspaceData();
        return () => workspaceRequestRef.current?.abort();
    }, [loadWorkspaceData]);

    useEffect(() => {
        componentMountedRef.current = true;
        return () => {
            componentMountedRef.current = false;
            window.clearTimeout(metadataSaveTimerRef.current);
        };
    }, []);

    useEffect(() => {
        const refreshWorkspace = (event) => {
            if (event.detail?.projectId && event.detail.projectId !== projectId) return;
            loadWorkspaceData({ silent: true });
        };

        window.addEventListener('foerderbuddy:project-updated', refreshWorkspace);
        return () => window.removeEventListener('foerderbuddy:project-updated', refreshWorkspace);
    }, [loadWorkspaceData, projectId]);

    const updateProjectStatus = async (nextStatus) => {
        if (!nextStatus || nextStatus === workspaceData?.project?.status) return;

        const previousData = workspaceData;
        setStatusSaving(true);
        setStatusMessage('');
        setWorkspaceData((currentData) => currentData
            ? {
                ...currentData,
                project: {
                    ...currentData.project,
                    status: nextStatus
                }
            }
            : currentData
        );

        try {
            const response = await axios.patch(`/api/airtable/project/${projectId}/status`, { status: nextStatus });
            const savedStatus = response.data?.status || nextStatus;
            setWorkspaceData((currentData) => currentData
                ? {
                    ...currentData,
                    project: {
                        ...currentData.project,
                        status: savedStatus
                    }
                }
                : currentData
            );
            onProjectUpdated?.(projectId, { status: savedStatus });
            setStatusMessage(`Status gespeichert: ${savedStatus}`);
        } catch (error) {
            console.error('Projektstatus konnte nicht gespeichert werden:', error.response ? error.response.data : error.message);
            setWorkspaceData(previousData);
            setStatusMessage(error.response?.data?.error || 'Status konnte nicht gespeichert werden.');
        } finally {
            setStatusSaving(false);
        }
    };

    const project = useMemo(() => workspaceData?.project || {}, [workspaceData]);

    useEffect(() => {
        if (!workspaceData || metadataProjectRef.current === projectId) return;
        const initialMetadata = {
            name: workspaceData.project?.name || '',
            companyId: workspaceData.company?.id || '',
            fundingProgramId: workspaceData.fundingProgram?.id || ''
        };
        metadataProjectRef.current = projectId;
        metadataDraftRef.current = initialMetadata;
        metadataSavedRef.current = initialMetadata;
        setMetadataDraft(initialMetadata);
        setMetadataSaveState({ status: 'idle', message: '' });
    }, [projectId, workspaceData]);

    useEffect(() => {
        if (activeSection !== 'dashboard' || !metadataEditing || metadataOptions.loaded) return;
        let active = true;
        setMetadataOptions((current) => ({ ...current, loading: true, error: '' }));
        Promise.all([
            axios.get('/api/airtable/unternehmen'),
            axios.get('/api/airtable/funding-programs')
        ]).then(([companiesResponse, programsResponse]) => {
            if (!active) return;
            const companies = (companiesResponse.data?.records || [])
                .map((record) => ({ id: record.id, name: getCompanyOptionName(record) }))
                .sort((left, right) => left.name.localeCompare(right.name, 'de'));
            const programs = (programsResponse.data?.programs || [])
                .map((program) => ({ id: program.id, name: program.programCode || program.name || 'Förderprogramm' }))
                .sort((left, right) => left.name.localeCompare(right.name, 'de'));
            setMetadataOptions({ companies, programs, loading: false, loaded: true, error: '' });
        }).catch((error) => {
            if (!active) return;
            console.error('Projektstammdaten-Optionen konnten nicht geladen werden:', error.response ? error.response.data : error.message);
            setMetadataOptions({ companies: [], programs: [], loading: false, loaded: true, error: 'Kunden und Programme konnten nicht geladen werden.' });
        });
        return () => { active = false; };
    }, [activeSection, metadataEditing, metadataOptions.loaded]);

    const flushProjectMetadata = useCallback(async () => {
        window.clearTimeout(metadataSaveTimerRef.current);
        if (metadataSavingRef.current || !projectId) return;

        metadataSavingRef.current = true;
        try {
            while (componentMountedRef.current && !projectMetadataEquals(metadataDraftRef.current, metadataSavedRef.current)) {
                const snapshot = { ...metadataDraftRef.current };
                const payload = { ...snapshot, name: snapshot.name.trim() };
                if (!payload.name) {
                    setMetadataSaveState({ status: 'error', message: 'Der Projektname darf nicht leer sein.' });
                    break;
                }

                setMetadataSaveState({ status: 'saving', message: 'Änderungen werden gespeichert…' });
                try {
                    const response = await axios.patch(`/api/airtable/project/${projectId}/metadata`, payload);
                    metadataSavedRef.current = snapshot;
                    if (!componentMountedRef.current) return;

                    const savedProject = response.data?.project || {};
                    setWorkspaceData((currentData) => currentData ? {
                        ...currentData,
                        project: { ...currentData.project, name: savedProject.name || snapshot.name, projectName: savedProject.name || snapshot.name, title: savedProject.name || snapshot.name },
                        company: response.data?.company || currentData.company,
                        fundingProgram: response.data?.fundingProgram || null
                    } : currentData);
                    onProjectUpdated?.(projectId, {
                        name: savedProject.name || snapshot.name,
                        company: response.data?.company?.name || '',
                        fundingProgram: response.data?.fundingProgram?.programCode || response.data?.fundingProgram?.name || ''
                    });

                    if (projectMetadataEquals(metadataDraftRef.current, snapshot)) {
                        setMetadataSaveState({ status: 'saved', message: 'Alle Änderungen gespeichert.' });
                    }
                } catch (error) {
                    console.error('Projektstammdaten konnten nicht gespeichert werden:', error.response ? error.response.data : error.message);
                    if (componentMountedRef.current) {
                        setMetadataSaveState({ status: 'error', message: error.response?.data?.error || 'Änderungen konnten nicht gespeichert werden.' });
                    }
                    break;
                }
            }
        } finally {
            metadataSavingRef.current = false;
        }
    }, [onProjectUpdated, projectId]);

    const updateProjectMetadata = (field, value) => {
        const nextMetadata = { ...metadataDraftRef.current, [field]: value };
        metadataDraftRef.current = nextMetadata;
        setMetadataDraft(nextMetadata);
        setMetadataSaveState({ status: 'pending', message: 'Änderungen noch nicht gespeichert…' });
        window.clearTimeout(metadataSaveTimerRef.current);
        metadataSaveTimerRef.current = window.setTimeout(flushProjectMetadata, 600);
    };
    const projectForAction = useMemo(() => ({
        ...project,
        projectQuestions: workspaceData?.projectQuestions || project.projectQuestions || [],
        workflowDecision: workspaceData?.workflowDecision || project.workflowDecision,
        workflowCore: workspaceData?.workflowCore || project.workflowCore,
        fundingDecision: workspaceData?.fundingDecision || project.fundingDecision,
        evidenceGraph: workspaceData?.evidenceGraph || project.evidenceGraph,
        fundingProgram: workspaceData?.fundingProgram?.programCode || project.recommendedProgram || project.fundingProgram
    }), [project, workspaceData]);
    const action = getProjectActionCenter(projectForAction);
    const fundingDecision = workspaceData?.fundingDecision || project.fundingDecision;
    const workflowCore = workspaceData?.workflowCore || project.workflowCore;
    const evidenceGraph = workspaceData?.evidenceGraph || project.evidenceGraph;
    const operationalState = workspaceData?.operationalState || project.operationalState;
    const decisionTone = getDecisionTone(fundingDecision?.decision);
    const actionPath = action ? getProjectActionPath(projectId, action) : `/project/${projectId}/dashboard`;
    const compactAuditWorkspace = activeSection === 'audit';
    const workspaceCompanyLabel = loading
        ? 'Kontext wird geladen…'
        : workspaceError
            ? 'Kontext nicht verfügbar'
            : workspaceData?.company?.name || 'Unternehmen noch offen';
    const workspaceProgramLabel = loading
        ? 'Programm wird geladen…'
        : workspaceData?.fundingProgram?.programCode || project.recommendedProgram || 'Programm noch offen';

    const openOperationalDialog = () => {
        setOperationalForm({
            eventKey: operationalState?.quickActions?.[0]?.key || '',
            waitingOnLabel: '',
            expectedAt: '',
            followUpAt: '',
            responsible: operationalState?.nextInternalAction?.responsible || project.projectResponsible || '',
            note: ''
        });
        setOperationalDialogOpen(true);
        setStatusMessage('');
    };

    useEffect(() => {
        if (!operationalState || searchParams.get('operational') !== 'update') return;
        openOperationalDialog();
        const nextParams = new URLSearchParams(searchParams);
        nextParams.delete('operational');
        setSearchParams(nextParams, { replace: true });
    }, [operationalState, searchParams, setSearchParams]); // eslint-disable-line react-hooks/exhaustive-deps

    const saveOperationalStatus = async (event) => {
        event.preventDefault();
        if (!operationalForm.eventKey) return;
        setOperationalSaving(true);
        setStatusMessage('');
        try {
            const response = await axios.post(`/api/workflow/project/${projectId}/operational-event`, operationalForm);
            const nextOperationalState = response.data?.operationalState;
            setWorkspaceData((currentData) => currentData ? {
                ...currentData,
                operationalState: nextOperationalState || currentData.operationalState,
                project: {
                    ...currentData.project,
                    operationalState: nextOperationalState || currentData.project?.operationalState
                }
            } : currentData);
            setOperationalDialogOpen(false);
            setStatusMessage(response.data?.task
                ? 'Projektstand gespeichert und Wiedervorlage aktualisiert.'
                : 'Projektstand gespeichert.');
            window.dispatchEvent(new CustomEvent('foerderbuddy:project-updated', {
                detail: { projectId, source: 'operational-status' }
            }));
            onProjectUpdated?.(projectId, { operationalState: nextOperationalState });
        } catch (error) {
            console.error('Operativer Projektstand konnte nicht gespeichert werden:', error.response ? error.response.data : error.message);
            setStatusMessage(error.response?.data?.error || 'Operativer Projektstand konnte nicht gespeichert werden.');
        } finally {
            setOperationalSaving(false);
        }
    };
    const workspaceContextValue = useMemo(() => ({
        projectId,
        workspaceData,
        loading,
        error: workspaceError,
        reloadWorkspace: loadWorkspaceData,
        setWorkspaceData
    }), [projectId, workspaceData, loading, workspaceError, loadWorkspaceData]);

    return (
        <ProjectWorkspaceProvider value={workspaceContextValue}>
        <div className="project-workspace-shell">
            <aside className="project-workspace-sidebar" aria-label="Projektbereiche">
                <nav className="project-workspace-global-nav" aria-label="Hauptnavigation">
                    <span>Förderbuddy</span>
                    <Link to="/workflow">Heute</Link>
                    <Link to="/projects">Mandate</Link>
                    <Link to="/crm">Kunden</Link>
                    <Link to="/revenue">Umsatz</Link>
                    <Link to="/finance">Finanzen</Link>
                </nav>
                <div className="project-workspace-project-card">
                    <span>Aktuelles Mandat</span>
                    <strong>{workspaceCompanyLabel}</strong>
                    <small>
                        {workspaceProgramLabel}
                        {' · '}
                        {loading ? 'Status wird geladen…' : project.status || 'Status offen'}
                    </small>
                </div>

                <nav className="project-workspace-nav" aria-label="Projektbereiche">
                    <Link
                        to={`/project/${projectId}/dashboard`}
                        className={activeSection === 'dashboard' ? 'project-workspace-nav-active project-workspace-dashboard-link' : 'project-workspace-dashboard-link'}
                    >
                        Projekt-Cockpit
                    </Link>
                    {NAV_GROUPS.map((group) => (
                        <details className="project-workspace-nav-group" key={group.label} open={group.items.some((item) => item.id === activeSection) || undefined}>
                            <summary>
                                <span>{group.step}</span>
                                <strong>{group.label}</strong>
                                <small>{group.items.some((item) => item.id === activeSection) ? 'Aktuelle Phase' : `${group.items.length} Bereiche`}</small>
                            </summary>
                            <div>
                                {group.items.map((item) => (
                                    <Link
                                        key={item.id}
                                        to={`/project/${projectId}/${item.path}`}
                                        className={activeSection === item.id ? 'project-workspace-nav-active' : ''}
                                    >
                                        {item.label}
                                    </Link>
                                ))}
                            </div>
                        </details>
                    ))}
                </nav>

                <div className="project-workspace-utility-nav">
                    <details>
                        <summary>System & Wissen</summary>
                        <Link to="/funding-database" className="project-workspace-system-link">Förderdatenbank</Link>
                        <Link to="/ai-configuration" className="project-workspace-system-link">KI-Konfiguration</Link>
                        <Link to="/schema-health" className="project-workspace-system-link">Systemcheck</Link>
                    </details>
                </div>
            </aside>

            <div className="project-workspace-main">
                <header className={compactAuditWorkspace ? 'project-workspace-command project-workspace-command-audit' : 'project-workspace-command'}>
                    <div className="project-workspace-command-copy">
                        <p>Mandate / {workspaceCompanyLabel}</p>
                        <div className="project-workspace-title-row">
                            <h1>{loading ? 'Mandat wird geladen…' : project.name || 'Projekt'}</h1>
                            {activeSection === 'dashboard' && !loading && (
                                <button
                                    type="button"
                                    className="project-workspace-metadata-toggle"
                                    onClick={() => setMetadataEditing((current) => !current)}
                                    aria-expanded={metadataEditing}
                                >
                                    {metadataEditing ? 'Bearbeitung schließen' : 'Projektdaten bearbeiten'}
                                </button>
                            )}
                        </div>
                        {activeSection === 'dashboard' && metadataEditing && (
                            <section className="project-workspace-metadata-editor" aria-label="Projektstammdaten bearbeiten">
                                <label>
                                    <span>Projektname</span>
                                    <input
                                        value={metadataDraft.name}
                                        onChange={(event) => updateProjectMetadata('name', event.target.value)}
                                        onBlur={flushProjectMetadata}
                                        maxLength={200}
                                        autoFocus
                                    />
                                </label>
                                <label>
                                    <span>Kunde</span>
                                    <select
                                        value={metadataDraft.companyId}
                                        onChange={(event) => updateProjectMetadata('companyId', event.target.value)}
                                        onBlur={flushProjectMetadata}
                                        disabled={metadataOptions.loading}
                                    >
                                        <option value="">Kein Kunde zugewiesen</option>
                                        {workspaceData?.company?.id && !metadataOptions.companies.some((company) => company.id === workspaceData.company.id) && (
                                            <option value={workspaceData.company.id}>{workspaceData.company.name}</option>
                                        )}
                                        {metadataOptions.companies.map((company) => <option key={company.id} value={company.id}>{company.name}</option>)}
                                    </select>
                                </label>
                                <label>
                                    <span>Programm</span>
                                    <select
                                        value={metadataDraft.fundingProgramId}
                                        onChange={(event) => updateProjectMetadata('fundingProgramId', event.target.value)}
                                        onBlur={flushProjectMetadata}
                                        disabled={metadataOptions.loading}
                                    >
                                        <option value="">Programm noch offen</option>
                                        {workspaceData?.fundingProgram?.id && !metadataOptions.programs.some((program) => program.id === workspaceData.fundingProgram.id) && (
                                            <option value={workspaceData.fundingProgram.id}>{workspaceData.fundingProgram.programCode || workspaceData.fundingProgram.name}</option>
                                        )}
                                        {metadataOptions.programs.map((program) => <option key={program.id} value={program.id}>{program.name}</option>)}
                                    </select>
                                </label>
                                <div className={`project-workspace-metadata-save project-workspace-metadata-save-${metadataSaveState.status}`} role="status" aria-live="polite">
                                    <span aria-hidden="true" />
                                    {metadataSaveState.message || 'Änderungen werden automatisch gespeichert.'}
                                </div>
                                {metadataOptions.error && <small className="project-workspace-metadata-error">{metadataOptions.error}</small>}
                            </section>
                        )}
                        <div className="project-workspace-meta">
                            <span>{workspaceCompanyLabel}</span>
                            <span>{workspaceProgramLabel}</span>
                            <label className={`project-workspace-status-control ${statusSaving ? 'project-workspace-status-saving' : ''}`}>
                                <span>Projektstatus</span>
                                <select
                                    value={project.status || ''}
                                    onChange={(event) => updateProjectStatus(event.target.value)}
                                    disabled={loading || statusSaving}
                                    aria-label="Projektstatus ändern"
                                >
                                    <option value="" disabled>Status offen</option>
                                    {PROJECT_STATUSES.map((status) => (
                                        <option key={status} value={status}>{status}</option>
                                    ))}
                                </select>
                            </label>
                        </div>
                        {statusMessage && <small className="project-workspace-status-message">{statusMessage}</small>}
                    </div>

                    {!compactAuditWorkspace && <div className="project-workspace-decision-strip">
                        <Link to={actionPath} className="project-workspace-core-phase">
                            <span>Workflow</span>
                            <strong>{workflowCore?.label || action?.title || 'Workflow offen'}</strong>
                        </Link>
                        <Link to={`/project/${projectId}/dashboard`} className={`project-workspace-decision project-workspace-decision-${decisionTone}`}>
                            <span>Entscheidung</span>
                            <strong>{fundingDecision?.decision || 'Noch offen'}</strong>
                        </Link>
                        <Link to={`/project/${projectId}/dashboard`} className="project-workspace-score">
                            <span>Nachweise</span>
                            <strong>{evidenceGraph?.summary?.confidence ?? '–'}/100</strong>
                        </Link>
                    </div>}

                    {!compactAuditWorkspace && (operationalState ? (
                        <OperationalStatusPanel state={operationalState} projectId={projectId} onEdit={openOperationalDialog} />
                    ) : (
                        <div className={`project-workspace-next-action project-workspace-next-action-${action?.tone || 'neutral'}`}>
                            <div>
                                <span>Nächster Schritt</span>
                                <strong>{action?.title || 'Projekt prüfen'}</strong>
                                <small>{action?.detail || 'Öffne das Dashboard für die aktuelle Einordnung.'}</small>
                            </div>
                            <Link to={actionPath}>{action?.linkLabel || 'Dashboard öffnen'}</Link>
                        </div>
                    ))}
                </header>

                {workspaceError && (
                    <section className="project-workspace-context-error" role="alert">
                        <div>
                            <strong>Der Mandatskontext konnte nicht vollständig geladen werden.</strong>
                            <span>{workspaceError} Detailbereiche können weiterhin eigene Daten zeigen; Statusänderungen erst nach erfolgreichem Neuladen vornehmen.</span>
                        </div>
                        <button type="button" onClick={() => loadWorkspaceData()} disabled={loading}>Erneut laden</button>
                    </section>
                )}

                {operationalDialogOpen && (
                    <div className="project-operational-dialog-backdrop" role="presentation" onMouseDown={() => !operationalSaving && setOperationalDialogOpen(false)}>
                        <section className="project-operational-dialog" role="dialog" aria-modal="true" aria-labelledby="project-operational-dialog-title" onMouseDown={(event) => event.stopPropagation()}>
                            <div className="project-operational-dialog-head">
                                <div>
                                    <span>Operativer Projektstand</span>
                                    <h2 id="project-operational-dialog-title">Was ist gerade passiert?</h2>
                                    <p>Ein Ereignis aktualisiert Meilenstein, Ballbesitz und Wiedervorlage gemeinsam.</p>
                                </div>
                                <button type="button" onClick={() => setOperationalDialogOpen(false)} disabled={operationalSaving} aria-label="Dialog schließen">×</button>
                            </div>
                            <form onSubmit={saveOperationalStatus}>
                                <label className="project-operational-dialog-wide">
                                    Statusereignis
                                    <select value={operationalForm.eventKey} onChange={(event) => setOperationalForm((current) => ({ ...current, eventKey: event.target.value }))} required>
                                        {(operationalState?.quickActions || []).map((item) => (
                                            <option key={item.key} value={item.key}>{item.label}</option>
                                        ))}
                                    </select>
                                </label>
                                <label className="project-operational-dialog-wide">
                                    Konkretisierung <small>optional</small>
                                    <input value={operationalForm.waitingOnLabel} onChange={(event) => setOperationalForm((current) => ({ ...current, waitingOnLabel: event.target.value }))} placeholder="z. B. Personalkostendaten 2025 zuliefern" />
                                </label>
                                <label>
                                    Erwartet bis <small>extern</small>
                                    <input type="date" value={operationalForm.expectedAt} onChange={(event) => setOperationalForm((current) => ({ ...current, expectedAt: event.target.value }))} />
                                </label>
                                <label>
                                    Interne Wiedervorlage
                                    <input type="date" value={operationalForm.followUpAt} onChange={(event) => setOperationalForm((current) => ({ ...current, followUpAt: event.target.value }))} />
                                </label>
                                <label className="project-operational-dialog-wide">
                                    Verantwortlich danach
                                    <input value={operationalForm.responsible} onChange={(event) => setOperationalForm((current) => ({ ...current, responsible: event.target.value }))} placeholder="mawe-co Verantwortlicher" />
                                </label>
                                <label className="project-operational-dialog-wide">
                                    Notiz <small>optional</small>
                                    <textarea value={operationalForm.note} onChange={(event) => setOperationalForm((current) => ({ ...current, note: event.target.value }))} placeholder="Quelle oder relevante Details" />
                                </label>
                                <p className="project-operational-dialog-hint">Leere Termine werden anhand des Ereignisses automatisch mit einer passenden Standardfrist gesetzt.</p>
                                <div className="project-operational-dialog-actions">
                                    <button type="button" onClick={() => setOperationalDialogOpen(false)} disabled={operationalSaving}>Abbrechen</button>
                                    <button type="submit" disabled={operationalSaving || !operationalForm.eventKey}>{operationalSaving ? 'Speichert...' : 'Stand speichern'}</button>
                                </div>
                            </form>
                        </section>
                    </div>
                )}

                {!compactAuditWorkspace && !loading && workspaceData?.decisionGate && (
                    <div id="project-workflow-gate" className="project-workspace-gate-system">
                        <WorkflowGatePanel
                            projectId={projectId}
                            initialGate={workspaceData.decisionGate}
                            title="Project Decision System"
                            compact
                            onSaved={() => loadWorkspaceData({ silent: true })}
                        />
                    </div>
                )}

                <main className="project-workspace-content">
                    {children}
                </main>
            </div>
        </div>
        </ProjectWorkspaceProvider>
    );
};

export default ProjectWorkspaceShell;
